import { getAuthToken, waitForAuthReady, isAuthReady } from '../utils/authUtils';
import { store } from '../redux/store';
import { selectUser, setUser } from '../redux/slices/authSlice';
import requestDeduplicationService from './requestDeduplicationService';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Comprehensive data sanitization utility to convert Firestore Timestamps and handle non-serializable data
 * @param {any} data - Data to sanitize
 * @returns {any} Sanitized data
 */
const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const sanitized = { ...data };
  
  Object.keys(sanitized).forEach(key => {
    const value = sanitized[key];
    
    // Check if value is a Firestore Timestamp
    if (value && typeof value === 'object' && value._seconds !== undefined && value._nanoseconds !== undefined) {
      // Convert Timestamp to ISO string for Redux serialization
      const date = new Date(value._seconds * 1000 + value._nanoseconds / 1000000);
      sanitized[key] = date.toISOString();
    } 
    // Handle Date objects - convert to ISO string for Redux serialization
    else if (value instanceof Date) {
      sanitized[key] = value.toISOString();
    }
    // Handle nested objects and arrays
    else if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        sanitized[key] = value.map(item => 
          typeof item === 'object' ? sanitizeData(item) : item
        );
      } else {
        sanitized[key] = sanitizeData(value);
      }
    }
  });
  
  return sanitized;
};

/**
 * Get doctors/attending physicians for a specific facility
 * This is a simpler endpoint that doesn't require admin permissions
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Doctors data
 */
export const getDoctorsByFacility = async (facilityId) => {
  try {
    if (!facilityId) {
      console.warn('getDoctorsByFacility: No facilityId provided');
      return { doctors: [] };
    }

    const url = `${API_BASE_URL}/internal/doctors?facilityId=${facilityId}`;
    const cacheKey = requestDeduplicationService.generateCacheKey('GET', url, { facilityId });

    // Check for pending request
    if (requestDeduplicationService.isRequestPending(cacheKey)) {
      console.log('getDoctorsByFacility: Request already in progress, waiting...');
      return await requestDeduplicationService.trackRequest(cacheKey);
    }

    // Check cache (valid for 2 minutes)
    const cachedResponse = requestDeduplicationService.getCachedResponse(cacheKey);
    if (cachedResponse) {
      console.log('getDoctorsByFacility: Using cached response');
      return cachedResponse;
    }

    const token = await getAuthToken();

    console.log('getDoctorsByFacility: Fetching doctors for facility:', facilityId);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.error('getDoctorsByFacility: Response not ok:', response.status);
      throw new Error(`Failed to fetch doctors: ${response.status}`);
    }

    const result = await response.json();
    console.log('getDoctorsByFacility: Fetched', result.doctors?.length || 0, 'doctors');

    const sanitizedResult = sanitizeData(result);
    
    // Cache the result for 2 minutes
    requestDeduplicationService.cacheResponse(cacheKey, sanitizedResult, 120000);
    requestDeduplicationService.completeRequest(cacheKey, sanitizedResult);

    return sanitizedResult;
  } catch (error) {
    console.error('Error fetching doctors by facility:', error);
    return { doctors: [] }; // Return empty array instead of throwing
  }
};

/**
 * Get users for admin management with role-based access control
 * @returns {Promise<Object>} Users data with facility assignments and details
 */
export const getUsersForManagement = async (retryCount = 0, maxRetries = 3) => {
  try {
    console.log('getUsersForManagement: Starting... (attempt', retryCount + 1, 'of', maxRetries + 1, ')');
    
    // Wait for authentication to be ready before making API calls
    if (!isAuthReady()) {
      console.log('getUsersForManagement: Auth not ready, waiting...');
      const authReady = await waitForAuthReady(10000); // Wait up to 10 seconds
      if (!authReady) {
        throw new Error('Authentication not ready. Please refresh the page and try again.');
      }
    }
    
    const token = await getAuthToken(5, 1500); // 5 retries, 1.5s delay
    console.log('getUsersForManagement: Got auth token:', token ? 'present' : 'missing');
    
    const url = `${API_BASE_URL}/admin/users/management`;
    console.log('getUsersForManagement: Calling API at:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('getUsersForManagement: Response status:', response.status);
    console.log('getUsersForManagement: Response ok:', response.ok);
    console.log('getUsersForManagement: Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Access denied. Admin or facility admin access required.');
      } else if (response.status === 500) {
        throw new Error('Server error. Please refresh the page and try again.');
      } else {
        throw new Error('Failed to fetch users');
      }
    }

    const result = await response.json();
    console.log('getUsersForManagement: Raw API response:', result);
    console.log('getUsersForManagement: Response type:', typeof result);
    console.log('getUsersForManagement: Response keys:', Object.keys(result || {}));
    console.log('getUsersForManagement: Response has users property:', 'users' in result);
    console.log('getUsersForManagement: Users property type:', typeof result.users);
    console.log('getUsersForManagement: Users property is array:', Array.isArray(result.users));
    
    // Process the new payload structure where each user contains embedded facilities
    const processedResult = {
      users: result.users || [],
      total: result.total || 0
    };
    
    console.log('getUsersForManagement: Processed result:', processedResult);
    console.log('getUsersForManagement: Users array length:', processedResult.users.length);
    console.log('getUsersForManagement: First user sample:', processedResult.users[0]);
    
    // Extract facilities from user objects for backward compatibility
    const allFacilities = new Map();
    const facilityAssignments = {};
    
    processedResult.users.forEach(user => {
      // Extract facilities from user.facilities array
      if (user.facilities && Array.isArray(user.facilities)) {
        user.facilities.forEach(facility => {
          allFacilities.set(facility.id, facility);
        });
        
        // Create facilityAssignments mapping for backward compatibility
        facilityAssignments[user.id] = user.facilityIds || [];
      }
    });
    
    // Add backward compatibility fields
    processedResult.facilityDetails = Object.fromEntries(allFacilities);
    processedResult.facilityAssignments = facilityAssignments;
    
    const sanitizedResult = sanitizeData(processedResult);
    console.log('getUsersForManagement: Final sanitized result:', sanitizedResult);
    console.log('getUsersForManagement: Final users count:', sanitizedResult.users?.length || 0);
    console.log('getUsersForManagement: Final first user sample:', sanitizedResult.users?.[0]);
    
    return sanitizedResult;
  } catch (error) {
    console.error('Error fetching users for management:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    // Retry on auth-related errors
    const isAuthError = error.message?.includes('Authentication') || 
                        error.message?.includes('authenticated') ||
                        error.message?.includes('token') ||
                        error.message?.includes('Server error');
    
    if (isAuthError && retryCount < maxRetries) {
      console.log(`getUsersForManagement: Auth error detected, retrying in 2 seconds... (attempt ${retryCount + 2})`);
      await new Promise(resolve => setTimeout(resolve, 2000));
      return getUsersForManagement(retryCount + 1, maxRetries);
    }
    
    throw error;
  }
};

/**
 * Get user profile by ID
 * @param {string} userId - The user ID
 * @param {boolean} forceRefresh - Force a refresh from API even if data exists in Redux
 * @returns {Promise<Object>} User profile data
 */
export const getUserProfile = async (userId, forceRefresh = false) => {
  try {
    // Check Redux store first
    const state = store.getState();
    const user = selectUser(state);

    // Check if this is a new user login (user changed or no profileChecked flag)
    const isNewUserLogin = !user?.uid || user.uid !== userId || !user.profileChecked;
    
    // For navigation data (history and pinned items), always fetch fresh data
    // This ensures pinned items persist correctly across sessions
    const shouldForceRefresh = forceRefresh || isNewUserLogin;
    
    // Return cached data only if not forcing refresh and user is the same
    if (!shouldForceRefresh && user?.uid === userId && user.profileChecked) {
      console.log('Using cached user profile from Redux');
      return user;
    }

    // Check deduplication service for pending requests
    const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE_URL}/internal/users/profile`, { userId });
    
    if (requestDeduplicationService.isRequestPending(cacheKey)) {
      console.log('User profile request already in progress, waiting for completion...');
      return await requestDeduplicationService.trackRequest(cacheKey);
    }

    // Check for cached response - but be more aggressive about refreshing for new logins
    const cachedResponse = requestDeduplicationService.getCachedResponse(cacheKey);
    if (cachedResponse && !shouldForceRefresh) {
      console.log('Using cached user profile response');
      return cachedResponse;
    }

    // Fetch from API
    console.log('Fetching user profile from API');
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const profile = data.profile || data;
    
    // Log the navigation data we received from the backend
    console.log('getUserProfile: Received profile data:', {
      hasNavigationHistory: !!profile.navigationHistory,
      navigationHistoryLength: profile.navigationHistory?.length || 0,
      hasPinnedItems: !!profile.pinnedNavigationItems,
      pinnedItemsLength: profile.pinnedNavigationItems?.length || 0,
      profileKeys: Object.keys(profile || {})
    });

    // Sanitize the profile data to convert Firestore Timestamps
    const sanitizedProfile = sanitizeData(profile);

    // Cache the response
    requestDeduplicationService.cacheResponse(cacheKey, sanitizedProfile, 60000); // Cache for 1 minute

    // Update Redux store with fresh data
    if (user?.uid === userId) {
      store.dispatch(setUser({
        ...user,
        ...sanitizedProfile,
        profileChecked: true
      }));
    }

    // Complete the tracked request
    requestDeduplicationService.completeRequest(cacheKey, sanitizedProfile);

    return sanitizedProfile;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    
    // Complete the tracked request with error
    const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE_URL}/internal/users/profile`, { userId });
    requestDeduplicationService.completeRequest(cacheKey, null, error);
    
    throw error;
  }
};

/**
 * Clear user profile cache for a specific user
 * This is useful when we need to force a fresh fetch of user data
 * @param {string} userId - The user ID to clear cache for
 */
export const clearUserProfileCache = (userId) => {
  try {
    // Clear from Redux store
    const state = store.getState();
    const user = selectUser(state);
    
    if (user?.uid === userId) {
      // Reset the profileChecked flag to force a fresh fetch
      store.dispatch(setUser({
        ...user,
        profileChecked: false
      }));
      console.log('Cleared user profile cache for user:', userId);
    }
    
    // Clear from request deduplication service
    const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE_URL}/internal/users/profile`, { userId });
    requestDeduplicationService.clearCacheForPattern(cacheKey);
    
  } catch (error) {
    console.error('Error clearing user profile cache:', error);
  }
};

/**
 * Update user profile
 * @param {string} userId - The user ID
 * @param {Object} profileData - The profile data to update
 * @returns {Promise<Object>} Updated profile data
 */
export const updateUserProfile = async (userId, profileData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/${userId}/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(profileData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Assign facility to user
 * @param {string} userEmail - The user's email
 * @param {string} facilityId - The facility ID to assign
 * @returns {Promise<Object>} Assignment result
 */
export const assignFacilityToUser = async (userEmail, facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/assign-facility`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userEmail,
        facilityId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error assigning facility to user:', error);
    throw error;
  }
};

/**
 * Get user's language preference
 * @returns {Promise<Object>} User language preference data
 */
export const getUserLanguagePreference = async () => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/language-preference`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching user language preference:', error);
    throw error;
  }
};

/**
 * Update user's language preference
 * @param {string} language - The language preference
 * @returns {Promise<Object>} Updated language preference data
 */
export const updateUserLanguagePreference = async (language) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/language-preference`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ language })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating user language preference:', error);
    throw error;
  }
};

/**
 * Get user's accessible facilities
 * @param {number} page - Page number for pagination (default: 1)
 * @param {number} limit - Number of facilities per page (default: 50)
 * @returns {Promise<Object>} Object containing facilities array, total count, page, and limit
 */
export const getUserFacilities = async (page = 1, limit = 50) => {
  try {
    // Check deduplication service for pending requests
    const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE_URL}/internal/users/facilities`, { page, limit });
    
    if (requestDeduplicationService.isRequestPending(cacheKey)) {
      console.log('User facilities request already in progress, waiting for completion...');
      return await requestDeduplicationService.trackRequest(cacheKey);
    }

    // Check for cached response
    const cachedResponse = requestDeduplicationService.getCachedResponse(cacheKey);
    if (cachedResponse) {
      console.log('Using cached user facilities response');
      return cachedResponse;
    }

    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/facilities?page=${page}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Cache the response for 2 minutes (facilities don't change often)
    requestDeduplicationService.cacheResponse(cacheKey, data, 120000);
    
    // Complete the tracked request
    requestDeduplicationService.completeRequest(cacheKey, data);

    return data;
  } catch (error) {
    console.error('Error fetching user facilities:', error);
    
    // Complete the tracked request with error
    const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE_URL}/internal/users/facilities`, { page, limit });
    requestDeduplicationService.completeRequest(cacheKey, null, error);
    
    throw error;
  }
};

/**
 * Get user's facility assignments
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of facility IDs assigned to the user
 */
export const getUserFacilityAssignments = async (userId) => {
  try {
    // Check deduplication service for pending requests
    const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE_URL}/internal/users/${userId}/facility-assignments`);
    
    if (requestDeduplicationService.isRequestPending(cacheKey)) {
      console.log('User facility assignments request already in progress, waiting for completion...');
      return await requestDeduplicationService.trackRequest(cacheKey);
    }

    // Check for cached response
    const cachedResponse = requestDeduplicationService.getCachedResponse(cacheKey);
    if (cachedResponse) {
      console.log('Using cached user facility assignments response');
      return cachedResponse;
    }

    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/${userId}/facility-assignments`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const facilityIds = data.facilityIds || [];
    
    // Cache the response for 2 minutes
    requestDeduplicationService.cacheResponse(cacheKey, facilityIds, 120000);
    
    // Complete the tracked request
    requestDeduplicationService.completeRequest(cacheKey, facilityIds);

    return facilityIds;
  } catch (error) {
    console.error('Error fetching user facility assignments:', error);
    
    // Complete the tracked request with error
    const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE_URL}/internal/users/${userId}/facility-assignments`);
    requestDeduplicationService.completeRequest(cacheKey, null, error);
    
    throw error;
  }
};

/**
 * Assign facilities to a user
 * @param {string} userId - The user ID
 * @param {Array<string>} facilityIds - Array of facility IDs to assign
 * @returns {Promise<Object>} Assignment result
 */
export const assignUserFacilities = async (userId, facilityIds) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/${userId}/facility-assignments`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ facilityIds })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error assigning facilities to user:', error);
    throw error;
  }
};

/**
 * Get bulk user facility assignments for multiple users
 * @param {Array<string>} userIds - Array of user IDs
 * @returns {Promise<Object>} Object with user IDs as keys and facility assignments as values
 */
export const getBulkUserFacilityAssignments = async (userIds) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/bulk-facility-assignments`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userIds })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching bulk user facility assignments:', error);
    throw error;
  }
};

/**
 * Get users with optional facility filtering and pagination
 * @param {string} facilityId - Optional facility ID to filter users
 * @param {number} page - Page number for pagination (default: 1)
 * @param {number} limit - Number of users per page (default: 50)
 * @returns {Promise<Object>} Object containing users array, total count, page, and limit
 */
export const getUsers = async (facilityId = null, page = 1, limit = 50) => {
  try {
    const token = await getAuthToken();
    let url = `${API_BASE_URL}/internal/users?page=${page}&limit=${limit}`;
    
    if (facilityId) {
      url += `&facilityId=${facilityId}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('getUsers API response:', data);
    
    // Return the full response structure, but ensure users array is sanitized
    if (data.users && Array.isArray(data.users)) {
      const sanitizedUsers = data.users.map(user => sanitizeData(user));
      return {
        ...data,
        users: sanitizedUsers
      };
    } else if (Array.isArray(data)) {
      // Fallback for backward compatibility
      const sanitizedUsers = data.map(user => sanitizeData(user));
      return {
        users: sanitizedUsers,
        total: sanitizedUsers.length,
        page: page,
        limit: limit
      };
    } else {
      // Return empty result if no valid data
      return {
        users: [],
        total: 0,
        page: page,
        limit: limit
      };
    }
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Create a new user
 * @param {Object} userData - User data including email, password, name, role, etc.
 * @returns {Promise<Object>} Object containing the created user data
 */
export const createUser = async (userData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Get users assigned to a specific facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Array>} Array of users assigned to the facility
 */
export const getFacilityUsers = async (facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users?facilityId=${facilityId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const users = data.users || [];
    
    // Sanitize the users data to convert Firestore Timestamps
    const sanitizedUsers = users.map(user => sanitizeData(user));
    
    return sanitizedUsers;
  } catch (error) {
    console.error('Error fetching facility users:', error);
    throw error;
  }
}; 

/**
 * Update user navigation history
 * @param {string} userId - The user ID
 * @param {Array} navigationHistory - The navigation history array
 * @returns {Promise<Object>} Updated user data
 */
export const updateUserNavigationHistory = async (userId, navigationHistory) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/${userId}/navigation-history`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ navigationHistory })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating user navigation history:', error);
    throw error;
  }
};

/**
 * Update user pinned navigation items
 * @param {string} userId - The user ID
 * @param {Array} pinnedItems - The pinned items array
 * @returns {Promise<Object>} Updated user data
 */
export const updateUserPinnedItems = async (userId, pinnedItems) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/${userId}/pinned-items`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ pinnedItems })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating user pinned items:', error);
    throw error;
  }
};

/**
 * Get current user data
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} Current user data with properties: id, name, displayName, email, defaultFacility, role, etc.
 */
export const getCurrentUserData = async (userId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const userData = await response.json();
    
    // Sanitize the user data to convert Firestore Timestamps
    const sanitizedUserData = sanitizeData(userData);
    
    // Ensure the returned object has the expected structure
    return {
      id: sanitizedUserData.id || userId,
      name: sanitizedUserData.name || sanitizedUserData.displayName || '',
      displayName: sanitizedUserData.displayName || sanitizedUserData.name || '',
      email: sanitizedUserData.email || '',
      defaultFacility: sanitizedUserData.defaultFacility || null,
      role: sanitizedUserData.role || '',
      ...sanitizedUserData // Include any other properties from the response
    };
  } catch (error) {
    console.error('Error fetching current user data:', error);
    throw error;
  }
};

/**
 * Get all facilities (admin only)
 * @returns {Promise<Array>} Array of all facilities
 */
export const getAllFacilities = async () => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/facility/facilities`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const facilities = data.facilities || [];
    
    // Sanitize the facilities data to convert Firestore Timestamps
    const sanitizedFacilities = facilities.map(facility => sanitizeData(facility));
    
    return sanitizedFacilities;
  } catch (error) {
    console.error('Error fetching all facilities:', error);
    throw error;
  }
}; 